import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, Heart, Instagram, MapPin, MessageCircle } from "lucide-react";
import { getLocale } from "../../utils/content";
import styles from "./InstagramFeed.module.scss";

const feedUrl = `${process.env.PUBLIC_URL || ""}/data/instagram-feed.json`;

function formatInstagramDate(timestamp, locale) {
  return new Intl.DateTimeFormat(locale === "en" ? "en-US" : "pl-PL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(timestamp * 1000));
}

function getExcerpt(post, t) {
  if (post.excerpt) {
    return post.excerpt;
  }

  if (post.caption) {
    return post.caption;
  }

  if (post.location) {
    return post.location;
  }

  return t("instagram_empty_caption");
}

function getThumbnailSource(post) {
  return post.thumbnailPath || post.thumbnailUrl || post.imagePath || post.imageUrl;
}

export default function InstagramFeed() {
  const { t, i18n } = useTranslation();
  const locale = getLocale(i18n.language);
  const [feed, setFeed] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadFeed() {
      try {
        const response = await fetch(feedUrl, { cache: "no-store" });

        if (!response.ok) {
          throw new Error(`Feed request failed: ${response.status}`);
        }

        const payload = await response.json();

        if (!cancelled) {
          setFeed(payload);
          setError(false);
        }
      } catch {
        if (!cancelled) {
          setError(true);
        }
      }
    }

    loadFeed();

    return () => {
      cancelled = true;
    };
  }, []);

  const posts = feed?.items || [];
  const updatedAt = feed?.updatedAt ? new Date(feed.updatedAt) : null;

  return (
    <section className={styles.instagramFeed} aria-labelledby="instagram-feed-title">
      <div className={styles.header}>
        <div className={styles.copy}>
          <span className={styles.eyebrow}>{t("instagram_eyebrow")}</span>
          <h2 id="instagram-feed-title">{t("instagram_title")}</h2>
          {/*<p>{t("instagram_intro")}</p>*/}
        </div>

        <a
          href={feed?.profileUrl || "https://www.instagram.com/warsawhockeyclub/"}
          target="_blank"
          rel="noreferrer"
          className={styles.followLink}
        >
          <Instagram size={18} />
          <span>{t("instagram_follow")}</span>
        </a>
      </div>

      {posts.length > 0 ? (
        <>
          <div className={styles.grid}>
            {posts.map((post, index) => (
              <article
                key={post.id}
                className={`${styles.postCard} ${index === 0 ? styles.featured : ""}`}
              >
                <a
                  href={post.permalink}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.imageWrap}
                >
                  <img
                    src={getThumbnailSource(post)}
                    alt={getExcerpt(post, t)}
                    loading="lazy"
                    onError={(event) => {
                      const fallbackSource = post.imagePath || post.imageUrl;

                      if (event.currentTarget.src !== fallbackSource) {
                        event.currentTarget.src = fallbackSource;
                      }
                    }}
                  />
                  <span className={styles.openPill}>
                    <Instagram size={14} />
                    <span>{t("instagram_open_post")}</span>
                  </span>
                </a>

                <div className={styles.postBody}>
                  <div className={styles.metaTop}>
                    <span>{formatInstagramDate(post.timestamp, locale)}</span>
                    {post.location && (
                      <span className={styles.location}>
                        <MapPin size={14} />
                        <span>{post.location}</span>
                      </span>
                    )}
                  </div>

                  <p>{getExcerpt(post, t)}</p>

                  <div className={styles.postFooter}>
                    <div className={styles.stats}>
                      <span>
                        <Heart size={14} />
                        <span>{post.likesCount}</span>
                      </span>
                      <span>
                        <MessageCircle size={14} />
                        <span>{post.commentsCount}</span>
                      </span>
                    </div>

                    <a href={post.permalink} target="_blank" rel="noreferrer" className={styles.readLink}>
                      <span>{t("instagram_open_post")}</span>
                      <ArrowUpRight size={15} />
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {updatedAt && !Number.isNaN(updatedAt.getTime()) && (
            <p className={styles.updated}>
              {t("instagram_updated", {
                date: new Intl.DateTimeFormat(locale === "en" ? "en-US" : "pl-PL", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                }).format(updatedAt),
              })}
            </p>
          )}
        </>
      ) : (
        <div className={styles.emptyState}>
          <p>{error ? t("instagram_error") : t("instagram_loading")}</p>
          <a
            href="https://www.instagram.com/warsawhockeyclub/"
            target="_blank"
            rel="noreferrer"
            className={styles.followLink}
          >
            <Instagram size={18} />
            <span>{t("instagram_follow")}</span>
          </a>
        </div>
      )}
    </section>
  );
}
